import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { shipmentService, clientColorService, orderService, supplierService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { X, Truck, Plus, Trash2, Filter, Palette } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'pianificata', label: 'Pianificata', bg: 'bg-blue-100 text-blue-800' },
  { value: 'spedita', label: 'Spedita', bg: 'bg-emerald-100 text-emerald-800' },
  { value: 'annullata', label: 'Annullata', bg: 'bg-red-100 text-red-800' },
];

const DEST_TYPES = [
  { value: 'cliente', label: 'Cliente', color: '#3b82f6' },
  { value: 'fornitore', label: 'Fornitore', color: '#f97316' },
  { value: 'altro', label: 'Altro', color: '#64748b' },
];

const statusBadge = (s) => {
  const opt = STATUS_OPTIONS.find(o => o.value === s) || STATUS_OPTIONS[0];
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${opt.bg}`}>{opt.label}</span>;
};

const destTypeBadge = (t) => {
  const dt = DEST_TYPES.find(d => d.value === t) || DEST_TYPES[2];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dt.color }}></span>
      {dt.label}
    </span>
  );
};

export default function ShipmentCalendarPage() {
  const { user } = useAuth();
  const calRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [showDetail, setShowDetail] = useState(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterDestType, setFilterDestType] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  function emptyForm(date) {
    return {
      destination_type: 'cliente', client_id: '', supplier_id: '',
      shipment_date: date || '', shipment_time: '',
      title: '', notes: '', status: 'pianificata', order_id: '',
      custom_color: '',
    };
  }

  const loadClients = useCallback(async () => {
    try { const res = await clientColorService.getAll(); setClients(res.data); } catch {}
  }, []);

  const loadSuppliers = useCallback(async () => {
    try { const res = await supplierService.getAll(); setSuppliers(res.data || []); } catch {}
  }, []);

  const loadOrders = useCallback(async () => {
    try { const res = await orderService.getAll(); setOrders(res.data || []); } catch {}
  }, []);

  const loadEntries = useCallback(async (start, end) => {
    try {
      setLoading(true);
      const params = {};
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (filterDestType) params.destination_type = filterDestType;
      if (filterClient) params.client_id = filterClient;
      if (filterSupplier) params.supplier_id = filterSupplier;
      if (filterStatus) params.status = filterStatus;
      const res = await shipmentService.getAll(params);
      setEntries(res.data);
    } catch { toast.error('Errore caricamento spedizioni'); } finally { setLoading(false); }
  }, [filterDestType, filterClient, filterSupplier, filterStatus]);

  useEffect(() => { loadClients(); loadSuppliers(); loadOrders(); }, [loadClients, loadSuppliers, loadOrders]);
  useEffect(() => {
    const api = calRef.current?.getApi();
    if (api) {
      const view = api.view;
      loadEntries(view.activeStart?.toISOString()?.slice(0, 10), view.activeEnd?.toISOString()?.slice(0, 10));
    } else {
      loadEntries();
    }
  }, [loadEntries]);

  const calendarEvents = entries.map(e => {
    const start = e.shipment_time ? `${e.shipment_date}T${e.shipment_time}` : e.shipment_date;
    const color = e.display_color || e.custom_color || e.client_color || '#64748b';
    return {
      id: e.id,
      title: `${e.client_name || e.supplier_name || 'Altro'} – ${e.title}`,
      start,
      allDay: !e.shipment_time,
      backgroundColor: color,
      borderColor: color,
      textColor: '#fff',
      extendedProps: e,
    };
  });

  const handleDateSelect = (info) => {
    const date = info.startStr?.slice(0, 10) || '';
    const time = info.startStr?.includes('T') ? info.startStr.slice(11, 16) : '';
    setEditEntry(null);
    setForm({ ...emptyForm(date), shipment_time: time });
    setShowModal(true);
  };

  const handleEventClick = (info) => { setShowDetail(info.event.extendedProps); };

  const handleEventDrop = async (info) => {
    const entry = info.event.extendedProps;
    const newDate = info.event.startStr?.slice(0, 10);
    const newTime = info.event.startStr?.includes('T') ? info.event.startStr.slice(11, 16) : null;
    try {
      await shipmentService.update(entry.id, { shipment_date: newDate, shipment_time: newTime });
      toast.success('Spedizione spostata');
      refreshCalendar();
    } catch { info.revert(); toast.error('Errore spostamento'); }
  };

  const handleDatesSet = (info) => {
    loadEntries(info.startStr?.slice(0, 10), info.endStr?.slice(0, 10));
  };

  const refreshCalendar = () => {
    const api = calRef.current?.getApi();
    if (api) {
      const view = api.view;
      loadEntries(view.activeStart?.toISOString()?.slice(0, 10), view.activeEnd?.toISOString()?.slice(0, 10));
    } else { loadEntries(); }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setForm({
      destination_type: entry.destination_type || 'cliente',
      client_id: entry.client_id || '',
      supplier_id: entry.supplier_id || '',
      shipment_date: entry.shipment_date || '',
      shipment_time: entry.shipment_time || '',
      title: entry.title || '',
      notes: entry.notes || '',
      status: entry.status || 'pianificata',
      order_id: entry.order_id || '',
      custom_color: entry.custom_color || '',
    });
    setShowDetail(null);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.shipment_date || !form.title) {
      toast.error('Compila i campi obbligatori');
      return;
    }
    if (form.destination_type === 'cliente' && !form.client_id) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (form.destination_type === 'fornitore' && !form.supplier_id) {
      toast.error('Seleziona un fornitore');
      return;
    }
    const payload = {
      ...form,
      client_id: form.destination_type === 'cliente' ? form.client_id : null,
      supplier_id: form.destination_type === 'fornitore' ? form.supplier_id : null,
      order_id: form.order_id || null,
      shipment_time: form.shipment_time || null,
      custom_color: form.custom_color || null,
    };
    try {
      if (editEntry) {
        await shipmentService.update(editEntry.id, payload);
        toast.success('Spedizione aggiornata');
      } else {
        await shipmentService.create(payload);
        toast.success('Spedizione creata');
      }
      setShowModal(false);
      refreshCalendar();
    } catch { toast.error('Errore salvataggio'); }
  };

  const handleDelete = async (id) => {
    try {
      await shipmentService.delete(id);
      toast.success('Spedizione eliminata');
      setShowDetail(null);
      refreshCalendar();
    } catch { toast.error('Errore eliminazione'); }
  };

  const hasFilters = filterDestType || filterClient || filterSupplier || filterStatus;

  return (
    <div className="space-y-4" data-testid="shipment-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="shipment-title">Calendario Spedizioni</h1>
          <p className="text-sm text-slate-500 mt-0.5">Pianificazione spedizioni clienti e fornitori</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="filter-toggle">
            <Filter className="w-4 h-4 mr-1" /> Filtri
            {hasFilters && <span className="ml-1 w-2 h-2 rounded-full bg-blue-500 inline-block" />}
          </Button>
          <Button size="sm" onClick={() => { setEditEntry(null); setForm(emptyForm(new Date().toISOString().slice(0, 10))); setShowModal(true); }} data-testid="new-shipment-btn">
            <Plus className="w-4 h-4 mr-1" /> Nuova Spedizione
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap gap-3 items-end" data-testid="filters-panel">
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Tipo Destinatario</label>
            <select value={filterDestType} onChange={e => setFilterDestType(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[140px]" data-testid="filter-dest-type">
              <option value="">Tutti</option>
              {DEST_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cliente</label>
            <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[180px]" data-testid="filter-client">
              <option value="">Tutti i clienti</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.code} – {c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Fornitore</label>
            <select value={filterSupplier} onChange={e => setFilterSupplier(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[180px]" data-testid="filter-supplier">
              <option value="">Tutti i fornitori</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.code} – {s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Stato</label>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white min-w-[140px]" data-testid="filter-status">
              <option value="">Tutti</option>
              {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {hasFilters && (
            <button onClick={() => { setFilterDestType(''); setFilterClient(''); setFilterSupplier(''); setFilterStatus(''); }}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium pb-1">Azzera filtri</button>
          )}
        </div>
      )}

      {/* Color Legend */}
      <div className="flex flex-wrap gap-3 items-center" data-testid="color-legend">
        {DEST_TYPES.map(dt => (
          <div key={dt.value} className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: dt.color }} />
            <span>{dt.label}</span>
          </div>
        ))}
        <span className="text-slate-300 mx-1">|</span>
        {clients.slice(0, 6).map(c => (
          <div key={c.id} className="flex items-center gap-1.5 text-xs text-slate-600">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: c.color || '#3b82f6' }} />
            <span>{c.name}</span>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 shipment-calendar" data-testid="shipment-calendar">
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek' }}
          buttonText={{ today: 'Oggi', month: 'Mese', week: 'Settimana', day: 'Giorno', list: 'Lista' }}
          locale="it"
          firstDay={1}
          selectable editable eventStartEditable eventDurationEditable={false}
          selectMirror dayMaxEvents={4} weekNumbers={false}
          allDaySlot allDayText="Tutto il giorno"
          slotMinTime="06:00:00" slotMaxTime="20:00:00"
          slotDuration="00:30:00" slotLabelInterval="01:00:00"
          slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          height="auto" contentHeight={600}
          events={calendarEvents}
          select={handleDateSelect}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          datesSet={handleDatesSet}
          eventDisplay="block"
          eventContent={(arg) => (
            <div className="px-1 py-0.5 text-xs leading-tight truncate">
              <div className="font-semibold truncate">{arg.event.title}</div>
              {arg.timeText && <div className="opacity-80">{arg.timeText}</div>}
            </div>
          )}
        />
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <ShipmentFormModal
          form={form} setForm={setForm}
          clients={clients} suppliers={suppliers} orders={orders}
          isEdit={!!editEntry} onSubmit={handleSubmit}
          onClose={() => setShowModal(false)}
        />
      )}

      {/* Detail Modal */}
      {showDetail && (
        <ShipmentDetailModal
          entry={showDetail}
          onEdit={() => openEdit(showDetail)}
          onDelete={() => handleDelete(showDetail.id)}
          onClose={() => setShowDetail(null)}
        />
      )}

      <style>{`
        .shipment-calendar .fc { font-size: 13px; font-family: inherit; }
        .shipment-calendar .fc-toolbar-title { font-size: 1.1rem !important; font-weight: 700; color: #0f172a; }
        .shipment-calendar .fc-button { font-size: 12px !important; padding: 4px 10px !important; border-radius: 6px !important; }
        .shipment-calendar .fc-button-primary { background: #0f172a !important; border-color: #0f172a !important; }
        .shipment-calendar .fc-button-primary:not(.fc-button-active):hover { background: #1e293b !important; }
        .shipment-calendar .fc-button-primary.fc-button-active { background: #334155 !important; border-color: #334155 !important; }
        .shipment-calendar .fc-daygrid-day-number,
        .shipment-calendar .fc-col-header-cell-cushion { color: #334155; text-decoration: none; font-weight: 500; }
        .shipment-calendar .fc-event { border-radius: 4px !important; border: none !important; cursor: pointer; }
        .shipment-calendar .fc-timegrid-slot { height: 28px; }
        .shipment-calendar .fc-list-event-title a { color: inherit; text-decoration: none; }
        .shipment-calendar .fc-day-today { background-color: #f0f9ff !important; }
        .shipment-calendar .fc-highlight { background: #e0f2fe !important; }
      `}</style>
    </div>
  );
}


function ShipmentFormModal({ form, setForm, clients, suppliers, orders, isEdit, onSubmit, onClose }) {
  const QUICK_COLORS = ['#3b82f6', '#f97316', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f59e0b', '#64748b', '#0f172a'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="shipment-form-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Truck className="w-4 h-4" /> {isEdit ? 'Modifica Spedizione' : 'Nuova Spedizione'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {/* Tipo Destinatario */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Tipo Destinatario *</label>
            <div className="flex gap-2">
              {DEST_TYPES.map(dt => (
                <button key={dt.value} type="button"
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${form.destination_type === dt.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                  onClick={() => setForm(f => ({ ...f, destination_type: dt.value, client_id: '', supplier_id: '' }))}
                  data-testid={`dest-type-${dt.value}`}>
                  <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5" style={{ backgroundColor: dt.color }}></span>
                  {dt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Dynamic Destination Dropdown */}
          {form.destination_type === 'cliente' && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Cliente *</label>
              <select value={form.client_id} onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-slate-900 focus:outline-none"
                required data-testid="form-client">
                <option value="">Seleziona cliente...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.code} – {c.name}</option>)}
              </select>
            </div>
          )}
          {form.destination_type === 'fornitore' && (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Fornitore *</label>
              <select value={form.supplier_id} onChange={e => setForm(f => ({ ...f, supplier_id: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-slate-900 focus:outline-none"
                required data-testid="form-supplier">
                <option value="">Seleziona fornitore...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.code} – {s.name}</option>)}
              </select>
            </div>
          )}

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Data Spedizione *</label>
              <input type="date" value={form.shipment_date} onChange={e => setForm(f => ({ ...f, shipment_date: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                required data-testid="form-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Ora Spedizione</label>
              <input type="time" value={form.shipment_time} onChange={e => setForm(f => ({ ...f, shipment_time: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-900 focus:outline-none"
                data-testid="form-time" />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Titolo / Riferimento *</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-900 focus:outline-none"
              placeholder="es. Spedizione particolari lotto 2024-001"
              required data-testid="form-title" />
          </div>

          {/* Status + Order */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Stato</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-slate-900 focus:outline-none"
                data-testid="form-status">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Ordine Collegato</label>
              <select value={form.order_id} onChange={e => setForm(f => ({ ...f, order_id: e.target.value }))}
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-slate-900 focus:outline-none"
                data-testid="form-order">
                <option value="">Nessuno</option>
                {orders.map(o => <option key={o.id} value={o.id}>{o.order_number}</option>)}
              </select>
            </div>
          </div>

          {/* Custom Color — event-only override */}
          {form.destination_type === 'cliente' ? (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">
                <Palette className="w-3 h-3 inline mr-1" />Colore Evento
              </label>
              <p className="text-xs text-slate-400 mb-2">Il colore predefinito proviene dall'anagrafica cliente. Puoi sovrascriverlo solo per questo evento.</p>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-50 border border-slate-200">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: clients.find(c => c.id === form.client_id)?.color || '#3b82f6' }} />
                  <span className="text-xs text-slate-500">Anagrafica</span>
                </div>
                <span className="text-xs text-slate-300">|</span>
                {QUICK_COLORS.slice(0, 5).map(c => (
                  <button key={c} type="button"
                    className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${form.custom_color === c ? 'border-slate-900 scale-110' : 'border-slate-200'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm(f => ({ ...f, custom_color: f.custom_color === c ? '' : c }))}
                  />
                ))}
                {form.custom_color && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, custom_color: '' }))}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-1">Usa anagrafica</button>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">
                <Palette className="w-3 h-3 inline mr-1" />Colore Personalizzato
                <span className="text-slate-400 normal-case ml-1">(solo per questo evento)</span>
              </label>
              <div className="flex items-center gap-2 mt-1">
                {QUICK_COLORS.map(c => (
                  <button key={c} type="button"
                    className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${form.custom_color === c ? 'border-slate-900 scale-110 ring-2 ring-slate-900/20' : 'border-slate-200'}`}
                    style={{ backgroundColor: c }}
                    onClick={() => setForm(f => ({ ...f, custom_color: f.custom_color === c ? '' : c }))}
                  />
                ))}
                <input type="color" value={form.custom_color || '#f97316'}
                  onChange={e => setForm(f => ({ ...f, custom_color: e.target.value }))}
                  className="w-6 h-6 rounded cursor-pointer border border-slate-200"
                  title="Colore personalizzato" />
                {form.custom_color && (
                  <button type="button" onClick={() => setForm(f => ({ ...f, custom_color: '' }))}
                    className="text-xs text-slate-400 hover:text-slate-600 ml-1">Reset</button>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Note</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-900 focus:outline-none resize-y"
              rows={3} placeholder="Dettagli spedizione, corriere..." data-testid="form-notes" />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>Annulla</Button>
            <Button type="submit" size="sm" data-testid="form-submit">
              {isEdit ? 'Aggiorna' : 'Crea Spedizione'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


function ShipmentDetailModal({ entry, onEdit, onDelete, onClose }) {
  const destName = entry.destination_type === 'fornitore'
    ? `${entry.supplier_code || ''} – ${entry.supplier_name || 'N/D'}`
    : entry.destination_type === 'cliente'
      ? `${entry.client_code || ''} – ${entry.client_name || 'N/D'}`
      : 'Altro';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={e => e.stopPropagation()} data-testid="shipment-detail-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: entry.display_color || entry.custom_color || '#64748b' }} />
            Dettaglio Spedizione
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <DetailRow label="Tipo" value={destTypeBadge(entry.destination_type)} />
          <DetailRow label="Destinatario" value={destName} />
          <DetailRow label="Titolo" value={entry.title} />
          <DetailRow label="Data" value={entry.shipment_date} />
          <DetailRow label="Ora" value={entry.shipment_time || 'Non specificata'} />
          <DetailRow label="Stato" value={statusBadge(entry.status)} />
          {entry.order_number && <DetailRow label="Ordine" value={entry.order_number} />}
          {entry.custom_color && (
            <DetailRow label="Colore" value={
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: entry.custom_color }} />
                <span className="text-xs text-slate-500">{entry.custom_color}</span>
              </div>
            } />
          )}
          {entry.notes && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide block mb-1">Note</span>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg px-3 py-2">{entry.notes}</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
          <button onClick={onDelete} className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1" data-testid="detail-delete">
            <Trash2 className="w-3.5 h-3.5" /> Elimina
          </button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Chiudi</Button>
            <Button size="sm" onClick={onEdit} data-testid="detail-edit">Modifica</Button>
          </div>
        </div>
      </div>
    </div>
  );
}


function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide min-w-[90px] pt-0.5">{label}</span>
      <span className="text-sm text-slate-800">{value}</span>
    </div>
  );
}
